import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, useTheme, useMediaQuery, alpha } from '@mui/material';
import { format, parseISO, eachMonthOfInterval, subMonths } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';

// Chart.jsコンポーネント登録
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface VisitFrequencyChartProps {
  treatmentRecords: Array<{
    id: string;
    treatmentDate: string;
    totalAmount: number;
  }>;
  height?: number;
}

export const VisitFrequencyChart: React.FC<VisitFrequencyChartProps> = ({ 
  treatmentRecords,
  height 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = height || (isMobile ? 200 : 300);

  const chartData = useMemo(() => {
    // 過去12ヶ月のデータを生成
    const now = new Date();
    const startDate = subMonths(now, 11);
    const months = eachMonthOfInterval({
      start: startDate,
      end: now
    });

    // 月ごとの来店回数を集計
    const monthlyVisits = new Map<string, number>();
    const monthlyRevenue = new Map<string, number>();

    months.forEach(month => {
      const monthKey = format(month, 'yyyy-MM');
      monthlyVisits.set(monthKey, 0);
      monthlyRevenue.set(monthKey, 0);
    });

    treatmentRecords.forEach(record => {
      const date = parseISO(record.treatmentDate);
      const monthKey = format(date, 'yyyy-MM');
      
      if (monthlyVisits.has(monthKey)) {
        monthlyVisits.set(monthKey, (monthlyVisits.get(monthKey) || 0) + 1);
        monthlyRevenue.set(monthKey, (monthlyRevenue.get(monthKey) || 0) + record.totalAmount);
      }
    });

    const labels = months.map(month => 
      format(month, 'M月', { locale: ja })
    );

    const visitData = months.map(month => {
      const monthKey = format(month, 'yyyy-MM');
      return monthlyVisits.get(monthKey) || 0;
    });

    const revenueData = months.map(month => {
      const monthKey = format(month, 'yyyy-MM');
      const revenue = monthlyRevenue.get(monthKey) || 0;
      return revenue / 10000; // 万円単位に変換
    });

    return {
      labels,
      datasets: [
        {
          label: '来店回数',
          data: visitData,
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderWidth: 3,
          pointRadius: 5,
          pointHoverRadius: 8,
          pointBackgroundColor: theme.palette.background.paper,
          pointBorderColor: theme.palette.primary.main,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: theme.palette.primary.main,
          pointHoverBorderColor: theme.palette.background.paper,
          tension: 0.4,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: '売上（万円）',
          data: revenueData,
          borderColor: theme.palette.success.main,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: theme.palette.background.paper,
          pointBorderColor: theme.palette.success.main,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: theme.palette.success.main,
          pointHoverBorderColor: theme.palette.background.paper,
          tension: 0.4,
          fill: false,
          yAxisID: 'y1',
          borderDash: [5, 5],
        },
      ],
    };
  }, [treatmentRecords, theme]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: isMobile ? 11 : 12,
            family: theme.typography.fontFamily,
          },
          color: theme.palette.text.primary,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.datasetIndex === 0) {
                label += context.parsed.y + '回';
              } else {
                label += context.parsed.y.toFixed(1) + '万円';
              }
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: isMobile ? 10 : 11,
          },
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: !isMobile,
          text: '来店回数',
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
        grid: {
          color: alpha(theme.palette.divider, 0.3),
          display: true,
        },
        ticks: {
          color: theme.palette.primary.main,
          font: {
            size: isMobile ? 10 : 11,
          },
          stepSize: 1,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: !isMobile,
          text: '売上（万円）',
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: theme.palette.success.main,
          font: {
            size: isMobile ? 10 : 11,
          },
        },
      },
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ height: chartHeight, position: 'relative' }}>
        <Line options={options} data={chartData} />
      </Box>
    </motion.div>
  );
};